/**
 * Client ของ MRP Run (`/v1/mrp-runs`, P2-6e, D-100/D-101) — เขียนตาม router จริงที่
 * เซิร์ฟเวอร์ serve (`apps/api/src/cap_erp_api/modules/planning/router.py`) ไม่ใช่
 * จาก `docs/API_CONTRACT.md` เพียงอย่างเดียว (ดูหมายเหตุยาวใน `types.ts`)
 *
 * **client ใหม่ทั้งตัว ไม่ใช่ผู้ใช้ของ `lib/forecasts`** ด้วยเหตุผลเดียวกับที่ไฟล์นั้น
 * ไม่ใช้ซ้ำจาก `lib/sales`/`lib/procurement`: แต่ละ vertical ของ Work Order คนละใบ
 * เขียนโครง ชื่อฟังก์ชัน และชุดความล้มเหลวของตัวเอง การผูกให้ใช้ abstraction ร่วมกัน
 * จะทำให้วันที่ contract ฝั่งหนึ่งเปลี่ยน อีกฝั่งเปลี่ยนตามโดยไม่มีใครตั้งใจ
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ยังไม่มีรัน"** — รายการว่างกับ
 *   โหลดไม่สำเร็จต้องแยกกันให้ขาด
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว — header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * contract ของกลุ่มนี้มี**สี่** operation และไฟล์นี้ implement ครบทั้งสี่: `GET`/
 * `POST /v1/mrp-runs`, `GET /v1/mrp-runs/{run_id}` และ
 * `POST /v1/mrp-runs/{run_id}/release`
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateMrpRunBody,
  MrpRun,
  MrpRunExclusion,
  MrpRunListPage,
  MrpRunRelease,
  MrpRunSummary,
  PlannedOrder,
  ReleasedDocument,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/** เส้นทางจริงของกลุ่มนี้ — ตัด base prefix ออกแล้ว URL ที่ client เรียกขึ้นต้นด้วย
 * `/v1` เสมอ */
export const MRP_RUNS_PATH = '/v1/mrp-runs';

/**
 * เส้นทางของรันเดียว — `run_id` เป็น **UUID surrogate** (D-039)
 *
 * `encodeURIComponent` ไว้ด้วยแม้ UUID จะไม่มีอักขระที่ต้อง escape: ค่าที่เดินทางมา
 * ถึงที่นี่มาจาก payload ของเซิร์ฟเวอร์ และค่าที่อ่านมาแล้วแปะลง URL ดิบ ๆ คือวิธีที่
 * คำขอจะเปลี่ยน**เส้นทาง**ทั้งเส้นในวันที่ payload ไม่ใช่สิ่งที่คิดไว้
 */
export function mrpRunPath(runId: string): string {
  return `${MRP_RUNS_PATH}/${encodeURIComponent(runId)}`;
}

/** `POST /{run_id}/release` — เส้นเดียวที่เขียนออกนอกโมดูล planning (D-101) */
export function mrpRunReleasePath(runId: string): string {
  return `${mrpRunPath(runId)}/release`;
}

/**
 * พารามิเตอร์ของ `GET /v1/mrp-runs` ที่ router รับ — และไม่มีตัวที่ห้า
 *
 * `plant_code` **แคบลงได้เท่านั้น ไม่เคยกว้างขึ้น** — เซิร์ฟเวอร์กรองตาม
 * `plant_scope` ของผู้เรียกก่อนเสมอ และโรงงานนอกขอบเขตตอบ `403` ไม่ใช่หน้าว่าง
 */
export interface MrpRunListQuery {
  readonly plant_code?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * ประกอบ query string — คีย์ที่ไม่ได้ระบุ (หรือว่างเปล่า) ไม่เดินทางไปเลย
 *
 * `cursor` ที่เป็นสตริงว่างถูกทิ้ง ไม่ใช่ส่งไปเป็นค่าว่าง: cursor ว่างไม่ใช่ "หน้าแรก"
 * สำหรับเซิร์ฟเวอร์ แต่เป็นค่าที่มันจะปฏิเสธว่าไม่ใช่ cursor ที่ตนออกให้
 */
export function buildMrpRunListQuery(query: MrpRunListQuery = {}): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ router ของกลุ่มนี้ตอบได้ แยกเป็นสถานะของตัวเอง
 *
 * `403` มีสองความหมายที่ `details.rule` เป็นตัวแยก: `PLANT_OUTSIDE_SCOPE` คือ
 * **โรงงานที่ขอไม่อยู่ในขอบเขตของคุณ** (D-004) ส่วนไม่มี rule คือ **แผนกของคุณไม่ได้
 * ถือโมดูล `PLANNING` ในระดับนั้น**
 *
 * `rule-violation` (`422`) เป็นที่อยู่ของกฎส่วนใหญ่ของกลุ่มนี้ — `MPS_NOT_RELEASED`,
 * `MPS_NOT_FOUND`, `BOM_CYCLE_DETECTED`, `BOM_EXPLOSION_TOO_DEEP` ของการสร้างรัน และ
 * `MRP_RUN_NOT_CURRENT` ของการปล่อย — การกลืนมันเป็น `forbidden` จะส่งผู้กดไปขอสิทธิ์
 * เพิ่มเพื่อแก้สิ่งที่ไม่ใช่เรื่องสิทธิ์เลยแม้แต่น้อย
 *
 * `conflict` (`409`) เก็บไว้เป็นสถานะของตัวเองตามแนวเดียวกับ client อื่นทุกตัว แม้
 * ไม่มีเส้นใดในกลุ่มนี้ตอบ `409` จริงในวันที่เขียนไฟล์นี้ — `_WRITE_RESPONSES` ของ
 * router ประกาศไว้เป็นส่วนหนึ่งของ envelope มาตรฐาน และ `requestPayload()` อ่านมัน
 * เหมือนสถานะอื่นทุกตัว
 */
export type MrpRunFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type MrpRunListResult =
  | { readonly status: 'ready'; readonly page: MrpRunListPage }
  | MrpRunFailure;

export type MrpRunDetailResult =
  | { readonly status: 'ready'; readonly run: MrpRun }
  | MrpRunFailure;

export type MrpRunReleaseResult =
  | { readonly status: 'ready'; readonly release: MrpRunRelease }
  | MrpRunFailure;

export interface MrpRunRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ — ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับ client อื่นใช้ */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** `YYYY-MM-DD` ที่เป็นวันที่มีรูปถูกต้อง — ไม่ผ่าน `Date` แม้แต่ครั้งเดียว (เช็คแค่
 * รูปแบบที่นี่ การมีอยู่จริงของวันเป็นหน้าที่ของ `mrpView.ts` ถ้าจำเป็นต้องใช้) */
const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function requiredCalendarDate(value: unknown): string | null {
  const text = requiredString(value);
  return text !== null && CALENDAR_DATE_PATTERN.test(text) ? text : null;
}

/** จำนวนเต็มไม่ติดลบ — ใช้กับ `line_no`/`planned_order_count`/`released_document_count` */
function requiredNonNegativeInt(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

/** `next_cursor` ของ envelope — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีหน้าถัดไป" */
function parseCursor(raw: unknown): string | null | undefined {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw !== 'string') {
    return undefined;
  }
  return raw.trim() === '' ? null : raw;
}

/**
 * หนึ่ง planned order — **ทุกฟิลด์บังคับตาม `PlannedOrderResponse`**
 *
 * ต่างจาก parser ของ Forecast ตรงที่ schema ฝั่งนี้ (`extra="forbid"`, ไม่มี field
 * ไหนเป็น `Optional`) เป็นแหล่งความจริงที่ตรวจแล้วโดยตรง (อ่านจาก `schemas.py` ก่อน
 * เขียนไฟล์นี้) จึงบังคับครบทุกฟิลด์แทนที่จะปล่อยเป็น nullable แบบ Forecast ซึ่ง
 * `docs/API_CONTRACT.md` ไม่ได้พิมพ์รูป response ไว้ทีละฟิลด์
 */
export function parsePlannedOrder(raw: unknown): PlannedOrder | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const lineNo = requiredNonNegativeInt(record.line_no);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const requiredDate = requiredCalendarDate(record.required_date);
  const grossRequirementQty = requiredString(record.gross_requirement_qty);
  const projectedAvailableQty = requiredString(record.projected_available_qty);
  const netRequirementQty = requiredString(record.net_requirement_qty);
  const orderQty = requiredString(record.order_qty);
  const orderType = requiredString(record.order_type);
  const lotSizeProcedureUsed = requiredString(record.lot_size_procedure_used);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    lineNo === null ||
    productId === null ||
    productCode === null ||
    requiredDate === null ||
    grossRequirementQty === null ||
    projectedAvailableQty === null ||
    netRequirementQty === null ||
    orderQty === null ||
    orderType === null ||
    lotSizeProcedureUsed === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    line_no: lineNo,
    product_id: productId,
    product_code: productCode,
    required_date: requiredDate,
    gross_requirement_qty: grossRequirementQty,
    projected_available_qty: projectedAvailableQty,
    net_requirement_qty: netRequirementQty,
    order_qty: orderQty,
    order_type: orderType,
    lot_size_procedure_used: lotSizeProcedureUsed,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/** หนึ่งชิ้นส่วนที่ถูกงดเว้น — ทุกฟิลด์บังคับตาม `MrpRunExclusionResponse` */
export function parseMrpRunExclusion(raw: unknown): MrpRunExclusion | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const reason = requiredString(record.reason);
  const policyId = requiredString(record.policy_id);
  const createdAt = requiredString(record.created_at);

  if (
    productId === null ||
    productCode === null ||
    reason === null ||
    policyId === null ||
    createdAt === null
  ) {
    return null;
  }

  return {
    product_id: productId,
    product_code: productCode,
    reason,
    policy_id: policyId,
    created_at: createdAt,
  };
}

/**
 * หัวรันหนึ่งใบ **โดยไม่แตะ `planned_orders`/`exclusions` เลย** — ใช้กับแถวในรายการ
 *
 * ทุกฟิลด์บังคับตาม `MrpRunListItemResponse` ยกเว้น `superseded_by_run_id` ซึ่งเป็น
 * `Optional[uuid.UUID] = None` ฝั่งเซิร์ฟเวอร์จริง ๆ
 */
export function parseMrpRunSummary(raw: unknown): MrpRunSummary | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const runId = requiredString(record.run_id);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const mpsId = requiredString(record.mps_id);
  const mpsVersionLabel = requiredString(record.mps_version_label);
  const status = requiredString(record.status);
  const runAt = requiredString(record.run_at);
  const createdByEmployeeId = requiredString(record.created_by_employee_id);
  const plannedOrderCount = requiredNonNegativeInt(record.planned_order_count);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    runId === null ||
    plantId === null ||
    plantCode === null ||
    mpsId === null ||
    mpsVersionLabel === null ||
    status === null ||
    runAt === null ||
    createdByEmployeeId === null ||
    plannedOrderCount === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const supersededByRunIdRaw = record.superseded_by_run_id;
  const supersededByRunId =
    supersededByRunIdRaw === null || supersededByRunIdRaw === undefined
      ? null
      : requiredString(supersededByRunIdRaw);
  if (supersededByRunIdRaw !== null && supersededByRunIdRaw !== undefined && supersededByRunId === null) {
    return null;
  }

  return {
    run_id: runId,
    plant_id: plantId,
    plant_code: plantCode,
    mps_id: mpsId,
    mps_version_label: mpsVersionLabel,
    status,
    run_at: runAt,
    created_by_employee_id: createdByEmployeeId,
    planned_order_count: plannedOrderCount,
    superseded_by_run_id: supersededByRunId,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * รันเต็มใบ **พร้อม `planned_orders` และ `exclusions`** — `GET /{run_id}` และ `201`
 * ของ `POST`
 *
 * **ทั้งสองอาเรย์ต้องอ่านออกทุกแถว ไม่งั้นทั้งรันผิดรูป** แถวที่หายไปเงียบ ๆ หนึ่งแถว
 * คือ order หรือชิ้นส่วนที่ถูกงดเว้นที่ผู้วางแผนจะไม่มีวันเห็น
 */
export function parseMrpRun(raw: unknown): MrpRun | null {
  const summary = parseMrpRunSummary(raw);
  if (summary === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.planned_orders) || !Array.isArray(record.exclusions)) {
    return null;
  }

  const plannedOrders: PlannedOrder[] = [];
  for (const entry of record.planned_orders) {
    const order = parsePlannedOrder(entry);
    if (order === null) {
      return null;
    }
    plannedOrders.push(order);
  }

  const exclusions: MrpRunExclusion[] = [];
  for (const entry of record.exclusions) {
    const exclusion = parseMrpRunExclusion(entry);
    if (exclusion === null) {
      return null;
    }
    exclusions.push(exclusion);
  }

  return { ...summary, planned_orders: plannedOrders, exclusions };
}

/** อ่านรายการจาก envelope `{ items, next_cursor }` — และจากที่นั่นเท่านั้น
 *
 * แต่ละ item ถูกอ่านด้วย `parseMrpRunSummary()` ซึ่ง **ไม่แตะ `planned_orders`/
 * `exclusions` เลย** ต่อให้เซิร์ฟเวอร์ส่งมาด้วยก็ตาม — contract ตัดสองคีย์นั้นออก
 * จากรายการ และหน้าจอที่แอบใช้มันจากรายการได้ในวันหนึ่ง จะพังเงียบ ๆ ในวันที่
 * เซิร์ฟเวอร์เลิกส่งมา */
export function parseMrpRunListPage(raw: unknown): MrpRunListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: MrpRunSummary[] = [];
  for (const entry of record.items) {
    const parsed = parseMrpRunSummary(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

/** หนึ่งเอกสารที่ถูกปล่อย — ทุกฟิลด์บังคับตาม `ReleasedDocumentResponse` */
export function parseReleasedDocument(raw: unknown): ReleasedDocument | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const lineNo = requiredNonNegativeInt(record.line_no);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const requiredDate = requiredCalendarDate(record.required_date);
  const orderQty = requiredString(record.order_qty);
  const orderType = requiredString(record.order_type);
  const documentType = requiredString(record.document_type);
  const documentId = requiredString(record.document_id);
  const documentNumber = requiredString(record.document_number);
  const documentStatus = requiredString(record.document_status);
  const releasedByEmployeeId = requiredString(record.released_by_employee_id);
  const releasedAt = requiredString(record.released_at);

  if (
    lineNo === null ||
    productId === null ||
    productCode === null ||
    requiredDate === null ||
    orderQty === null ||
    orderType === null ||
    documentType === null ||
    documentId === null ||
    documentNumber === null ||
    documentStatus === null ||
    releasedByEmployeeId === null ||
    releasedAt === null
  ) {
    return null;
  }

  return {
    line_no: lineNo,
    product_id: productId,
    product_code: productCode,
    required_date: requiredDate,
    order_qty: orderQty,
    order_type: orderType,
    document_type: documentType,
    document_id: documentId,
    document_number: documentNumber,
    document_status: documentStatus,
    released_by_employee_id: releasedByEmployeeId,
    released_at: releasedAt,
  };
}

/** ผลของ `POST /{run_id}/release` — รัน + ทุกเอกสารที่มันผลิต */
export function parseMrpRunRelease(raw: unknown): MrpRunRelease | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const runId = requiredString(record.run_id);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const status = requiredString(record.status);
  const releasedDocumentCount = requiredNonNegativeInt(record.released_document_count);

  if (
    runId === null ||
    plantId === null ||
    plantCode === null ||
    status === null ||
    releasedDocumentCount === null ||
    !Array.isArray(record.documents)
  ) {
    return null;
  }

  const documents: ReleasedDocument[] = [];
  for (const entry of record.documents) {
    const document = parseReleasedDocument(entry);
    if (document === null) {
      return null;
    }
    documents.push(document);
  }

  return {
    run_id: runId,
    plant_id: plantId,
    plant_code: plantCode,
    status,
    released_document_count: releasedDocumentCount,
    documents,
  };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/**
 * แปลง body ของ `POST /v1/mrp-runs` ให้เหลือเฉพาะสองคีย์ที่ contract รับ
 *
 * เขียนคีย์ทีละตัวแทนการ spread state ของฟอร์ม: `extra = "forbid"` ฝั่งเซิร์ฟเวอร์
 * ทำให้คีย์แปลกปลอมที่ติดมากลายเป็น `400 MRP_RUN_FIELD_IS_SERVER_OWNED` ทั้งที่ผู้ใช้
 * กรอกถูกทุกช่อง
 */
export function serializeCreateMrpRunBody(
  body: CreateMrpRunBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    mps_version_label: body.mps_version_label,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | MrpRunFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: MrpRunRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/**
 * `GET /v1/mrp-runs` — **กรองตามขอบเขตโรงงานของผู้เรียกเสมอ** และ **ไม่มี
 * planned_orders/exclusions**
 *
 * **ไม่เรียงลำดับซ้ำเอง** และ **ไม่กรอง `SUPERSEDED` ทิ้ง** — รันที่ถูกแทนแล้วยังเป็น
 * รันที่มีอยู่จริงและยังตอบคำถามว่า "ตอนนั้นสั่งอะไรไปบ้างด้วยเลขอะไร" (D-100)
 */
export async function fetchMrpRuns(
  query: MrpRunListQuery = {},
  options: MrpRunRequestOptions = {},
): Promise<MrpRunListResult> {
  const result = await requestPayload(
    `${MRP_RUNS_PATH}${buildMrpRunListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseMrpRunListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `GET /v1/mrp-runs/{run_id}` — รันเดียว **พร้อม planned orders และ exclusions ครบ**
 *
 * เปิดได้ทุกสถานะ รวมทั้ง `SUPERSEDED` ซึ่งเป็นรันที่ D-100 เก็บไว้ให้ตอบว่าทำไมแผน
 * ครั้งก่อนถึงสั่งอย่างนั้น — ไม่มีเงื่อนไขพิเศษที่นี่ ใบที่ไม่มีอยู่หรืออยู่ในโรงงาน
 * นอกขอบเขตของผู้เรียกตอบ `404` เหมือนกันทั้งคู่ (D-004)
 */
export async function fetchMrpRun(
  runId: string,
  options: MrpRunRequestOptions = {},
): Promise<MrpRunDetailResult> {
  const result = await requestPayload(
    mrpRunPath(runId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const run = parseMrpRun(result.payload);
  return run === null ? { status: 'invalid-response' } : { status: 'ready', run };
}

/**
 * `POST /v1/mrp-runs` — รัน MRP ต่อแผนที่ `RELEASED` แล้วหนึ่งใบ ตอบ `201`
 *
 * **นี่คือครึ่งที่เสนอ ไม่ใช่ครึ่งที่ปล่อย (D-101)** — เส้นนี้แค่คำนวณและเขียนแผน
 * planned order ทั้งชุดในธุรกรรมเดียว ไม่แตะ Production Order หรือ Purchase
 * Requisition เลยแม้แต่แถวเดียว ผู้เรียกต้องกด "ปล่อย" แยกต่างหาก (`releaseMrpRun`)
 * เพื่อให้สิ่งนั้นเกิดขึ้นจริง
 */
export async function createMrpRun(
  body: CreateMrpRunBody,
  options: MrpRunRequestOptions = {},
): Promise<MrpRunDetailResult> {
  const result = await requestPayload(
    MRP_RUNS_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateMrpRunBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const run = parseMrpRun(result.payload);
  return run === null ? { status: 'invalid-response' } : { status: 'ready', run };
}

/**
 * `POST /v1/mrp-runs/{run_id}/release` — เปลี่ยนทุก planned order ของรัน `CURRENT`
 * ใบนี้ให้เป็นเอกสาร `DRAFT` จริง ตอบ `201`
 *
 * **ไม่มี body ใด ๆ ส่งไปเลย** — router ไม่ได้ประกาศฟิลด์ใดของคำขอนี้ เอกสารกี่ใบ
 * ชนิดไหนถูกตัดสินทั้งหมดจาก planned order ของรันเอง
 *
 * **การกระทำที่ **แยกจากการสร้างรันโดยสิ้นเชิง** และเป็นการกระทำเดียวในกลุ่มนี้ที่
 * เขียนออกนอกโมดูล planning (D-101) — ผู้เรียกไฟล์นี้ต้องเรียกฟังก์ชันนี้เอง ไม่มี
 * การเรียกต่อกันอัตโนมัติจาก `createMrpRun()` ที่ไหนเลยในไฟล์นี้
 *
 * **ปล่อยซ้ำรันที่ยังเป็น `CURRENT` ไม่สร้างอะไรใหม่** — เซิร์ฟเวอร์คืนเอกสารชุดเดิม
 * ที่ `201` เดิม (idempotent) ส่วนรันที่ถูกแทนไปแล้วระหว่างนั้นตอบ `422
 * MRP_RUN_NOT_CURRENT` พร้อม `details.current_status` และ
 * `details.superseded_by_run_id` — **นี่คือคำปฏิเสธของเซิร์ฟเวอร์ที่หน้าจอต้อง
 * เรนเดอร์เมื่อปล่อยซ้ำไม่สำเร็จ** ไม่ใช่สิ่งที่ client เดาหรือกันเองล่วงหน้า
 */
export async function releaseMrpRun(
  runId: string,
  options: MrpRunRequestOptions = {},
): Promise<MrpRunReleaseResult> {
  const result = await requestPayload(
    mrpRunReleasePath(runId),
    { method: 'POST', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const release = parseMrpRunRelease(result.payload);
  return release === null ? { status: 'invalid-response' } : { status: 'ready', release };
}

/** อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี */
export function ruleFromFailure(failure: MrpRunFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/** อ่าน `details.current_status` จากความล้มเหลว — สถานะที่รันนั้น **ถืออยู่จริง** */
export function currentStatusFromFailure(failure: MrpRunFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** อ่าน `details.superseded_by_run_id` จากความล้มเหลวของการปล่อย — `null` เมื่อไม่มี
 * (รวมถึงตอนที่รันนั้นยังไม่เคยถูกแทนที่เลย ซึ่งเซิร์ฟเวอร์ส่ง `null` มาเช่นกัน) */
export function supersededByRunIdFromFailure(failure: MrpRunFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.superseded_by_run_id;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}
